import React, { useState } from 'react';
import { Headphones, Mic, Drama, Zap, Bot, Clapperboard, Sparkles, Radio, Flame } from 'lucide-react';

export interface CharacterPreset {
  id: string;
  name: string;
  tagline: string;
  isPro: boolean;
  color: string;
  gradient: string;
  borderColor: string;
  imageUrl?: string;
  renderIcon: (size?: number) => React.ReactNode;
}

export const CHARACTER_PRESETS: CharacterPreset[] = [
  {
    id: 'char-maverick',
    name: 'Aria',
    tagline: 'Neon Pop',
    isPro: false,
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
    borderColor: 'rgba(236, 72, 153, 0.7)',
    imageUrl: '/avatars/singer_girl_1.png',
    renderIcon: (size = 20) => <Headphones size={size} color="#ffffff" />
  },
  {
    id: 'char-siren',
    name: 'Say',
    tagline: 'Acoustic Soul',
    isPro: false,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    borderColor: 'rgba(245, 158, 11, 0.7)',
    imageUrl: '/avatars/say.png?v=3',
    renderIcon: (size = 20) => <Mic size={size} color="#ffffff" />
  },
  {
    id: 'char-shadow',
    name: 'Kai',
    tagline: 'Modern Rock',
    isPro: false,
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
    borderColor: 'rgba(6, 182, 212, 0.7)',
    imageUrl: '/avatars/singer_boy_1.png',
    renderIcon: (size = 20) => <Drama size={size} color="#ffffff" />
  },
  {
    id: 'char-luna',
    name: 'Luna',
    tagline: 'Electro Dream',
    isPro: false,
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
    borderColor: 'rgba(139, 92, 246, 0.7)',
    imageUrl: '/avatars/singer_girl_2.png',
    renderIcon: (size = 20) => <Sparkles size={size} color="#ffffff" />
  },
  {
    id: 'char-titan',
    name: 'Titan',
    tagline: 'Epic Hero',
    isPro: false,
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
    borderColor: 'rgba(239, 68, 68, 0.7)',
    renderIcon: (size = 20) => <Zap size={size} color="#ffffff" />
  },
  {
    id: 'char-vox',
    name: 'V.O.X.',
    tagline: 'Synth Android',
    isPro: false,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    borderColor: 'rgba(59, 130, 246, 0.7)',
    renderIcon: (size = 20) => <Bot size={size} color="#ffffff" />
  },
  {
    id: 'char-maestro',
    name: 'Maestro',
    tagline: 'Sound Director',
    isPro: false,
    color: '#eab308',
    gradient: 'linear-gradient(135deg, #eab308 0%, #a855f7 100%)',
    borderColor: 'rgba(234, 179, 8, 0.7)',
    renderIcon: (size = 20) => <Clapperboard size={size} color="#ffffff" />
  },
  {
    id: 'char-echo',
    name: 'Echo',
    tagline: 'Lo-Fi Chill',
    isPro: false,
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
    borderColor: 'rgba(16, 185, 129, 0.7)',
    renderIcon: (size = 20) => <Radio size={size} color="#ffffff" />
  },
  {
    id: 'char-blaze',
    name: 'Blaze',
    tagline: 'Rhythm & Fire',
    isPro: false,
    color: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
    borderColor: 'rgba(249, 115, 22, 0.7)',
    renderIcon: (size = 20) => <Flame size={size} color="#ffffff" />
  }
];

interface CharacterAvatarProps {
  avatarId?: string;
  fallbackInitial?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showBadge?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const CharacterAvatar: React.FC<CharacterAvatarProps> = ({
  avatarId,
  fallbackInitial = 'P',
  size = 'md',
  showBadge = false,
  style
}) => {
  const [imageError, setImageError] = useState(false);
  const preset = CHARACTER_PRESETS.find((p) => p.id === avatarId) || CHARACTER_PRESETS[0];

  const sizePixels = {
    xs: 24,
    sm: 32,
    md: 44,
    lg: 58,
    xl: 76
  }[size];

  const iconPixels = {
    xs: 12,
    sm: 16,
    md: 22,
    lg: 28,
    xl: 38
  }[size];

  return (
    <div
      style={{
        width: `${sizePixels}px`,
        height: `${sizePixels}px`,
        borderRadius: '50%',
        background: preset.gradient,
        border: `2px solid ${preset.borderColor}`,
        boxShadow: `0 0 14px ${preset.borderColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        flexShrink: 0,
        overflow: 'hidden',
        ...style
      }}
      title={preset.name ? `${preset.name} (${preset.tagline})` : 'Performer Avatar'}
    >
      {preset.imageUrl && !imageError ? (
        <img
          src={preset.imageUrl}
          alt={preset.name}
          loading="eager"
          decoding="async"
          onError={() => setImageError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
            display: 'block'
          }}
        />
      ) : (
        preset.renderIcon(iconPixels)
      )}

      {showBadge && preset.isPro && (
        <div
          style={{
            position: 'absolute',
            bottom: '0',
            right: '0',
            background: '#f59e0b',
            borderRadius: '3px',
            padding: '1px 3px',
            fontSize: '7px',
            fontWeight: 800,
            color: '#000000',
            lineHeight: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.6)',
            zIndex: 2
          }}
        >
          PRO
        </div>
      )}
    </div>
  );
};
